import React, { useContext, useState } from 'react';
import { ChevronLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import superAdminServices from '../../../../services/superAdminServices';
import AuthContext from '../../../../context/AuthContext';


export default function AddOrganisation() {
    const { user, authTokens } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    organisation_name: '',
    organisation_description: '',
    email: '',
    first_name: '',
    last_name: '',
    owner_contact: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.organisation_name.trim()) {
      setError('Le nom de l\'organisation est requis');
      return false;
    }
    if (!formData.email.trim()) {
      setError('L\'email du propriétaire est requis');
      return false;
    }
    if (!formData.first_name.trim()) {
      setError('Le prénom du propriétaire est requis');
      return false;
    }
    if (!formData.last_name.trim()) {
      setError('Le nom du propriétaire est requis');
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Veuillez entrer une adresse email valide');
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    // Prepare data for backend - matches Django serializer
    const payload = {
      organisation_name: formData.organisation_name.trim(),
      organisation_description: formData.organisation_description.trim(),
      email: formData.email.trim(),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
    };

    // Call the service method
    const response = await superAdminServices.createOrganization(payload, authTokens.access);

    setLoading(false);

    if (response.success) {
      setSuccessMessage('Organisation créée avec succès! Un email a été envoyé à l\'administrateur.');
      
      // Clear form
      setFormData({
        organisation_name: '',
        organisation_description: '',
        email: '',
        first_name: '',
        last_name: '',
      });

      // Redirect after success
      setTimeout(() => {
        navigate('/admin-panel/organizations');
      }, 2000);
    } else {
      setError(response.error || 'Une erreur est survenue lors de la création de l\'organisation');
      console.error('Error:', response.error);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="p-5 sm:p-6 max-w-6xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={handleCancel}
          className="mr-3 p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Créer une organisation</h1>
          <p className="text-sm text-gray-400 mt-1">Créez une nouvelle organisation et son administrateur</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Form */}
      <div onSubmit={handleSubmit} className="space-y-6">
        {/* Two Section Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Organization Info Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Informations de l'organisation</h3>
            
            <div className="space-y-4">
              {/* Organisation Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Nom de l'organisation *
                </label>
                <input
                  type="text"
                  name="organisation_name"
                  value={formData.organisation_name}
                  onChange={handleInputChange}
                  placeholder="Ex: Acme Corp"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                />
              </div>

              {/* Organisation Description */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="organisation_description"
                  value={formData.organisation_description}
                  onChange={handleInputChange}
                  placeholder="Décrivez l'organisation..."
                  rows="4"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Admin User Info Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Informations de l'administrateur</h3>
            
            <div className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="admin@exemple.com"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                />
              </div>

              {/* First Name and Last Name */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder="Jean"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Dupont"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-4 p-3 bg-green-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-green-700">
                <strong>Note:</strong> Un mot de passe temporaire sera généré et un email de réinitialisation sera envoyé à l'utilisateur.
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Création en cours...' : 'Créer l\'organisation'}
          </button>
        </div>
      </div>
    </div>
  );
}